import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import { selectUser } from '@/store/selectors/user';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { redactSensitiveState, type RedactedState } from '@/utils/redactState.utils';
import type { SessionSnapshot } from '@/types/Diagnostics';

// Base selector
export const selectDebug = (state: RootState) => state.debug;

export const selectIsDebugPanelOpen = createSelector(selectDebug, (debug) => debug.isOpen);
export const selectDebugPanelHeight = createSelector(selectDebug, (debug) => debug.heightPx);
export const selectStorageEstimate = createSelector(selectDebug, (debug) => debug.storageEstimate);
export const selectBrowserInfo = createSelector(selectDebug, (debug) => debug.browserInfo);
export const selectLastRefreshedAt = createSelector(selectDebug, (debug) => debug.lastRefreshedAt);
export const selectLogEntries = createSelector(selectDebug, (debug) => debug.logEntries);
export const selectLogNamespaces = createSelector(selectDebug, (debug) => debug.logNamespaces);
export const selectLogLevel = createSelector(selectDebug, (debug) => debug.logLevel);
export const selectP2PDiagnostics = createSelector(selectDebug, (debug) => debug.p2pDiagnostics);

// Read-only session snapshot derived from the user/auth slices (no slice field).
export const selectSessionSnapshot = createSelector(
  selectUser,
  selectCurrentUserId,
  (user, userId): SessionSnapshot | null => {
    if (user === null) return null;

    const snapshot: SessionSnapshot = {
      userId: userId ?? user.id,
      name: user.name,
      organization: user.organization,
    };
    if (user.email !== undefined) snapshot.email = user.email;
    if (user.department !== undefined) snapshot.department = user.department;

    return snapshot;
  }
);

// Full-state read returning a redacted, serializable snapshot (hard rule #6).
export const selectDebugStateSnapshot = (state: RootState): RedactedState =>
  redactSensitiveState(state);
